import React, { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { Box, Paper, IconButton, ButtonBase } from "@mui/material"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import CloseIcon from "@mui/icons-material/Close"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import dayjs from "dayjs"
import { COLORS } from "../../../utils"

const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"]
const WEEKDAYS_FR = ["L", "M", "M", "J", "V", "S", "D"]
const RANGE_BG = "#E3F2FD"

function sameDay(a, b) {
  if (!a || !b) return false
  return dayjs(a).isSame(dayjs(b), "day")
}

function inRangeExclusive(d, start, end) {
  if (!start || !end) return false
  return dayjs(d).isAfter(dayjs(start), "day") && dayjs(d).isBefore(dayjs(end), "day")
}

function formatDMY(d) {
  if (!d) return ""
  return dayjs(d).format("DD/MM/YYYY")
}

const DateRangeFilter = ({ value = { start: null, end: null }, onChange }) => {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)
  const [view, setView] = useState(() => {
    const base = value.start ? dayjs(value.start) : dayjs()
    return { year: base.year(), month: base.month() }
  })
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 })
  const triggerRef = useRef(null)
  const popoverRef = useRef(null)

  useEffect(() => {
    if (open) setDraft(value)
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e) => {
      if (triggerRef.current?.contains(e.target) || popoverRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  const labelText = value.start && value.end
    ? sameDay(value.start, value.end)
      ? formatDMY(value.start)
      : `${formatDMY(value.start)} – ${formatDMY(value.end)}`
    : value.start
    ? formatDMY(value.start)
    : null

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setPopoverPos({ top: rect.bottom + 8, left: rect.left })
    }
    setOpen((o) => !o)
  }

  const clear = () => {
    onChange({ start: null, end: null })
    setOpen(false)
  }

  const onPickDay = (d) => {
    if (!draft.start || (draft.start && draft.end)) {
      const next = { start: d.toDate(), end: null }
      setDraft(next)
      onChange(next)
    } else {
      const picked = d.toDate()
      const next = d.isBefore(dayjs(draft.start), "day")
        ? { start: picked, end: draft.start }
        : { start: draft.start, end: picked }
      setDraft(next)
      onChange(next)
      setOpen(false)
    }
  }

  const navMonth = (delta) => {
    setView((v) => {
      const next = dayjs(new Date(v.year, v.month)).add(delta, "month")
      return { year: next.year(), month: next.month() }
    })
  }

  const firstOfMonth = dayjs(new Date(view.year, view.month, 1))
  const startWeekday = (firstOfMonth.day() + 6) % 7
  const daysInMonth = firstOfMonth.daysInMonth()
  const cells = []
  for (let i = 0; i < startWeekday; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(dayjs(new Date(view.year, view.month, d)))
  while (cells.length % 7 !== 0) cells.push(null)

  const today = dayjs().startOf("day")

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <ButtonBase
        ref={triggerRef}
        disableRipple
        onClick={handleToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
          borderBottom: open
            ? `2px solid ${COLORS.LABEL_GREY}`
            : "1px solid rgba(0,0,0,0.42)",
          pb: open ? "7px" : 1,
          minHeight: 32,
          justifyContent: "flex-start",
          "&:hover": { borderBottomColor: "rgba(0,0,0,0.87)" },
        }}
      >
        <Box
          sx={{
            flex: 1,
            fontSize: 13,
            color: labelText ? "text.primary" : "text.secondary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {labelText || "Rechercher"}
        </Box>
        {labelText && (
          <Box
            component="span"
            onClick={(e) => { e.stopPropagation(); onChange({ start: null, end: null }) }}
            sx={{
              display: "flex",
              alignItems: "center",
              color: "text.secondary",
              cursor: "pointer",
              "&:hover": { color: "text.primary" },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
        <CalendarTodayIcon
          sx={{ fontSize: 18, color: open ? COLORS.PRIMARY_COLOR : COLORS.LABEL_GREY }}
        />
      </ButtonBase>

      {open && createPortal(
        <Paper
          ref={popoverRef}
          elevation={8}
          sx={{
            position: "fixed",
            top: popoverPos.top,
            left: popoverPos.left,
            zIndex: 1300,
            width: 320,
            borderRadius: 0.5,
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: "8px 16px 8px" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, px: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 16, fontWeight: 500, color: "text.primary" }}>
                {MONTHS_FR[view.month].toLowerCase()} {view.year}
                <ArrowDropDownIcon sx={{ color: COLORS.LABEL_GREY }} />
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton size="small" onClick={() => navMonth(-1)}>
                  <ChevronLeftIcon sx={{ fontSize: 20, color: COLORS.LABEL_GREY }} />
                </IconButton>
                <IconButton size="small" onClick={() => navMonth(1)}>
                  <ChevronRightIcon sx={{ fontSize: 20, color: COLORS.LABEL_GREY }} />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 1 }}>
              {WEEKDAYS_FR.map((w, i) => (
                <Box key={i} sx={{ textAlign: "center", fontSize: 13, color: COLORS.LABEL_GREY, py: 1 }}>
                  {w}
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
              {cells.map((d, i) => {
                if (!d) return <Box key={i} sx={{ aspectRatio: "1", opacity: 0 }} />

                const isStart = sameDay(d, draft.start)
                const isEnd = sameDay(d, draft.end)
                const isSelected = isStart || isEnd
                const isToday = d.isSame(today, "day")
                const isInRange = inRangeExclusive(d, draft.start, draft.end)
                const isRangeStart = isStart && !!draft.end
                const isRangeEnd = isEnd && !!draft.start
                const showStrip = isInRange || isRangeStart || isRangeEnd

                return (
                  <Box
                    key={i}
                    onClick={() => onPickDay(d)}
                    sx={{
                      aspectRatio: "1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      cursor: "pointer",
                      "&::before": {
                        // eslint-disable-next-line quotes
                        content: '""',
                        position: "absolute",
                        top: "8px",
                        bottom: "8px",
                        left: isRangeStart ? "50%" : 0,
                        right: isRangeEnd ? "50%" : 0,
                        background: showStrip ? RANGE_BG : "transparent",
                        zIndex: 1,
                      },
                      "&:hover > div": {
                        bgcolor: isSelected ? COLORS.PRIMARY_COLOR : "action.hover",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 2,
                        position: "relative",
                        fontSize: 13,
                        color: isSelected ? "white" : "text.primary",
                        bgcolor: isSelected ? COLORS.PRIMARY_COLOR : "transparent",
                        fontWeight: isSelected ? 500 : 400,
                        border: isToday && !isSelected ? "1px solid rgba(0,0,0,0.3)" : "none",
                        transition: "background 120ms",
                      }}
                    >
                      {d.date()}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pb: 2 }}>
            <ButtonBase
              onClick={clear}
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: COLORS.PRIMARY_COLOR,
                px: 2,
                py: 1,
                borderRadius: 1,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                "&:hover": { bgcolor: RANGE_BG },
              }}
            >
              Effacer
            </ButtonBase>
          </Box>
        </Paper>,
        document.body
      )}
    </Box>
  )
}

export default DateRangeFilter
